/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Typography } from 'antd';

import { PlusOutlined } from '@ant-design/icons';

import DriverCard from './DriverCard';

import './DriverVehicleSelector.scss';

const { Text } = Typography;

function DriverVehicleSelector({ value = [], onChange, ...props }) {
  const [selectedDrivers, setSelectedDrivers] = useState(value || []);
  const isInternalUpdate = useRef(false);
  const {
    driverListOptions,
    vehicleListOptions,
    stages,
    fieldsPermissions,
    isTaskReadOnly,
    isEditing,
    task,
    transportCompanyId,
    onCreateDriver,
  } = props;

  const isFieldDisabled = (fieldName) =>
    task?.stage?.id === 5 ||
    fieldsPermissions?.[fieldName]?.access !== 2 ||
    isTaskReadOnly ||
    !isEditing;

  useEffect(() => {
    if (!isInternalUpdate.current) {
      setSelectedDrivers(value || []);
    }
    isInternalUpdate.current = false;
  }, [value]);

  const handleDriversChange = (newDrivers) => {
    isInternalUpdate.current = true;
    setSelectedDrivers(newDrivers);

    if (onChange) {
      const validDrivers = newDrivers.filter((d) => d.userId !== null && d.userId !== undefined);
      onChange(validDrivers.length > 0 ? validDrivers : []);
    }
  };

  const getAvailableDrivers = () => {
    const selectedDriverIds = selectedDrivers.filter((d) => d.userId !== null).map((d) => d.userId);
    return driverListOptions.filter((driver) => !selectedDriverIds.includes(driver.value));
  };

  const getAvailableVehicles = () => {
    const selectedVehicleIds = selectedDrivers
      .filter((d) => d.vehicle?.id != null)
      .map((d) => d.vehicle.id);
    return vehicleListOptions.filter((vehicle) => !selectedVehicleIds.includes(vehicle.value));
  };

  const handleAddDriverCard = () => {
    const waitingStage = stages.find(({ id }) => id === 1);

    const newEmptyDriver = {
      id: `temp-${Date.now()}-${Math.random()}`,
      userId: null,
      userName: null,
      stage: waitingStage,
      vehicle: null,
      startDate: null,
      endDate: null,
      totalTrainingTime: null,
      acceptanceDeadline: null,
      isNewCard: true,
    };

    handleDriversChange([...selectedDrivers, newEmptyDriver]);
  };

  const handleSelectDriverInCard = (cardIndex, driverId) => {
    const driver = driverListOptions.find((d) => d.value === driverId);
    if (!driver) return;

    const newDrivers = selectedDrivers.map((item, index) => {
      if (index === cardIndex) {
        return {
          ...item,
          userId: driver.id,
          userName: driver.name,
          isNewCard: false,
        };
      }
      return item;
    });
    handleDriversChange(newDrivers);
  };

  const handleRemoveDriver = (driverIndex) => {
    const newDrivers = selectedDrivers.filter((_, index) => index !== driverIndex);
    handleDriversChange(newDrivers);
  };

  const handleAddVehicleToDriver = (driverIndex, vehicleId) => {
    const vehicle = vehicleListOptions.find((v) => v.value === vehicleId);
    if (!vehicle) return;

    const newDrivers = selectedDrivers.map((driver, index) =>
      index === driverIndex ? { ...driver, vehicle } : driver
    );
    handleDriversChange(newDrivers);
  };

  const handleRemoveVehicleFromDriver = (driverIndex) => {
    const newDrivers = selectedDrivers.map((driver, index) =>
      index === driverIndex ? { ...driver, vehicle: null } : driver
    );
    handleDriversChange(newDrivers);
  };

  const buildDriverFromDrawer = (driverData, existingDriver = null) => {
    const waitingStage = stages?.find?.(({ id }) => id === 1) || null;
    const driverId = driverData?.userId ?? driverData?.id ?? driverData?.value;
    const driverName = driverData?.userName ?? driverData?.name ?? driverData?.label;

    if (!driverId) {
      return existingDriver;
    }

    if (existingDriver) {
      return {
        ...existingDriver,
        userId: driverId,
        userName: driverName,
        isNewCard: false,
      };
    }

    return {
      id: `temp-${Date.now()}-${Math.random()}`,
      userId: driverId,
      userName: driverName,
      stage: waitingStage,
      vehicle: null,
      startDate: null,
      endDate: null,
      totalTrainingTime: null,
      acceptanceDeadline: null,
      isNewCard: false,
    };
  };

  const buildVehicleFromDrawer = (vehicleData) => {
    if (!vehicleData) {
      return null;
    }

    const vehicleId = vehicleData?.id ?? vehicleData?.value;
    if (!vehicleId) {
      return null;
    }

    return {
      ...vehicleData,
      id: vehicleId,
      value: vehicleData?.value ?? vehicleId,
      label:
        vehicleData?.label ??
        `${vehicleData?.licensePlate || ''} - ${vehicleData?.type?.name || ''} - ${
          vehicleData?.capacity || 0
        } lugares`,
    };
  };

  const handleDriverCreatedFromDrawer = (driverIndex, creationResult) => {
    if (!creationResult) {
      return;
    }

    const { driver: driverData, vehicle: vehicleData } = creationResult;

    const hasExistingDriver = driverIndex !== null && driverIndex !== undefined;

    if (!hasExistingDriver || !selectedDrivers[driverIndex]) {
      const newDriver = buildDriverFromDrawer(driverData);
      if (!newDriver) {
        return;
      }

      if (vehicleData) {
        newDriver.vehicle = buildVehicleFromDrawer(vehicleData);
      }

      handleDriversChange([...selectedDrivers, newDriver]);
      return;
    }

    const newDrivers = selectedDrivers.map((driver, index) =>
      index === driverIndex
        ? {
            ...buildDriverFromDrawer(driverData, driver),
            vehicle: vehicleData ? buildVehicleFromDrawer(vehicleData) : driver.vehicle,
          }
        : driver
    );

    handleDriversChange(newDrivers);
  };

  const handleCreateDriverRequest = (driverIndex, companyIdFromCard) => {
    if (!onCreateDriver) {
      return;
    }

    const targetCompanyId = companyIdFromCard || transportCompanyId;
    if (!targetCompanyId) {
      return;
    }

    onCreateDriver({
      transportCompanyId: targetCompanyId,
      onSuccess: (driverData) => handleDriverCreatedFromDrawer(driverIndex, driverData),
    });
  };

  const handleOccasionalDriverCreated = (occasionalDriver, driverIndex) => {
    const createdVehicle = occasionalDriver.vehicle;
    const vehicle = {
      ...(createdVehicle || {}),
      licensePlate: createdVehicle?.licensePlate || occasionalDriver.placa,
      capacity: createdVehicle?.capacity || occasionalDriver.capacidade,
      type: createdVehicle?.type || {
        id: occasionalDriver.tipoId,
        name: occasionalDriver.tipo,
      },
      transportCompanyId: createdVehicle?.transportCompanyId,
      isOccasional: true,
      label: `${occasionalDriver.placa} - ${occasionalDriver.tipo} - ${occasionalDriver.capacidade} lugares`,
    };

    // Sem id de cadastro em /Vehicle — não aparece em Logística > Veículos
    delete vehicle.id;
    delete vehicle.value;

    const newDrivers = selectedDrivers.map((driver, index) => {
      if (index === driverIndex) {
        return {
          ...driver,
          userId: occasionalDriver.userId,
          userName: occasionalDriver.nome,
          vehicle,
          occasionalDriver,
          isOccasional: true,
        };
      }
      return driver;
    });

    handleDriversChange(newDrivers);
  };

  const hasDriversPermission = !isFieldDisabled('DriverList');
  const hasDriverOptions = driverListOptions.length > 0;
  const hasSelectedDrivers = selectedDrivers.length > 0;

  return (
    <div className="driver-vehicle-selector">
      {hasSelectedDrivers && (
        <div className="driver-vehicle-selector__cards-container">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {selectedDrivers.map((driver, index) => (
              <DriverCard
                key={driver.id || driver.userId || `driver-${index}`}
                driver={driver}
                driverIndex={index}
                availableDrivers={getAvailableDrivers()}
                availableVehicles={getAvailableVehicles()}
                isFieldDisabled={isFieldDisabled}
                onSelectDriverInCard={handleSelectDriverInCard}
                onRemoveDriver={handleRemoveDriver}
                onAddVehicleToDriver={handleAddVehicleToDriver}
                onRemoveVehicleFromDriver={handleRemoveVehicleFromDriver}
                onCreateDriver={(companyId) => handleCreateDriverRequest(index, companyId)}
                transportCompanyId={transportCompanyId}
                onOccasionalDriverCreated={handleOccasionalDriverCreated}
              />
            ))}
          </Space>
        </div>
      )}

      {hasDriversPermission && (
        <div className="driver-vehicle-selector__add-button-container">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddDriverCard}
            className="driver-vehicle-selector__add-button"
            block
          >
            Adicionar Motorista
          </Button>
        </div>
      )}

      {/* Estados vazios */}
      {!hasSelectedDrivers && hasDriversPermission && hasDriverOptions && (
        <div className="driver-vehicle-selector__empty-state">
          <Text type="secondary">Nenhum motorista adicionado</Text>
        </div>
      )}

      {!hasSelectedDrivers && hasDriversPermission && !hasDriverOptions && (
        <div className="driver-vehicle-selector__empty-state">
          <Text style={{ fontSize: '14px' }}>
            Selecione uma empresa de transporte para ver motoristas disponíveis
          </Text>
        </div>
      )}

      {!hasDriversPermission && !hasSelectedDrivers && (
        <div className="driver-vehicle-selector__empty-state">
          <Text type="secondary">Não é possível adicionar motoristas</Text>
        </div>
      )}
    </div>
  );
}

export default DriverVehicleSelector;
